/**
 * The ledger projection: State = fold(entries).
 *
 * Every entry is turned into an Effect: a set of ledger postings that sum to
 * zero (so the books always balance by construction) plus stock quantity and
 * value deltas per item. A void entry applies the exact negation of the
 * target's effect. Because every effect is made of integer deltas, `apply`
 * one entry at a time and `fold` over the whole list always agree.
 */

import {
  add,
  addQty,
  lineAmount,
  mulDivRound,
  neg,
  negQty,
  proportion,
  ZERO,
  ZERO_QTY,
  type Milli,
  type Paisa,
} from './money.js';
import type {
  Bin,
  Entry,
  EntryType,
  ReturnEntry,
  SaleEntry,
  PurchaseEntry,
  StockAdjustEntry,
} from './types.js';

// ---------------------------------------------------------------------------
// Ledger keys. Debit-positive convention everywhere: assets and expenses are
// normally positive, liabilities, capital and income normally negative, and
// the sum over all keys is always exactly 0.
// ---------------------------------------------------------------------------

export const LEDGER = {
  /** Sellable stock at cost. */
  STOCK: 'stock',
  /** Defective bin at cost, waiting to go back to a vendor. */
  DEFECTIVE: 'defective',
  /** Owner's opening capital (account, party and stock openings). */
  OPENING: 'opening',
  SALES: 'sales',
  SALES_RETURNS: 'sales_returns',
  COGS: 'cogs',
  EXPENSE: 'expense',
  DRAWING: 'drawing',
  /** Stock count corrections, damage write-offs, purchase-return gain/loss. */
  STOCK_ADJ: 'stock_adj',
} as const;

export const acctKey = (accountId: string): string => `acct:${accountId}`;
export const partyKey = (partyId: string): string => `party:${partyId}`;
export const isAcctKey = (k: string): boolean => k.startsWith('acct:');
export const isPartyKey = (k: string): boolean => k.startsWith('party:');
export const keyId = (k: string): string => k.slice(k.indexOf(':') + 1);

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface ItemState {
  readonly qty: Milli;
  readonly value: Paisa;
  readonly defQty: Milli;
  readonly defValue: Paisa;
  /** Last purchase / opening unit cost. Only a fallback for costing returns into an empty bin. */
  readonly lastUnitCost: Paisa;
}

export interface ItemDelta {
  readonly qty: Milli;
  readonly value: Paisa;
  readonly defQty: Milli;
  readonly defValue: Paisa;
}

export interface Effect {
  readonly type: EntryType;
  readonly ledgers: ReadonlyArray<readonly [string, Paisa]>;
  readonly items: ReadonlyArray<readonly [string, ItemDelta]>;
  /** Id of the void entry that reversed this effect, if any. */
  readonly voidedBy: string | null;
}

export interface State {
  readonly ledgers: ReadonlyMap<string, Paisa>;
  readonly items: ReadonlyMap<string, ItemState>;
  /** Effect of every applied entry, by entry id. Doubles as the audit trail. */
  readonly effects: ReadonlyMap<string, Effect>;
  readonly nextSaleNo: number;
  readonly nextPurchaseNo: number;
  readonly lastSeq: number;
  readonly count: number;
}

interface Draft {
  ledgers: Map<string, Paisa>;
  items: Map<string, ItemState>;
  effects: Map<string, Effect>;
  nextSaleNo: number;
  nextPurchaseNo: number;
  lastSeq: number;
  count: number;
}

export const FIRST_SALE_NO = 1001;
export const FIRST_PURCHASE_NO = 1;

export const EMPTY_ITEM: ItemState = {
  qty: ZERO_QTY,
  value: ZERO,
  defQty: ZERO_QTY,
  defValue: ZERO,
  lastUnitCost: ZERO,
};

export function emptyState(): State {
  return {
    ledgers: new Map(),
    items: new Map(),
    effects: new Map(),
    nextSaleNo: FIRST_SALE_NO,
    nextPurchaseNo: FIRST_PURCHASE_NO,
    lastSeq: 0,
    count: 0,
  };
}

export class InvariantError extends Error {
  override readonly name = 'InvariantError';
}

export function ledger(state: State, key: string): Paisa {
  return state.ledgers.get(key) ?? ZERO;
}

export function itemState(state: State, itemId: string): ItemState {
  return state.items.get(itemId) ?? EMPTY_ITEM;
}

export function isVoided(state: State, entryId: string): boolean {
  return state.effects.get(entryId)?.voidedBy != null;
}

// ---------------------------------------------------------------------------
// Effect computation
// ---------------------------------------------------------------------------

class EffectBuilder {
  private readonly ledgers = new Map<string, Paisa>();
  private readonly items = new Map<string, ItemDelta>();

  debit(key: string, amount: Paisa): void {
    this.ledgers.set(key, add(this.ledgers.get(key) ?? ZERO, amount));
  }

  credit(key: string, amount: Paisa): void {
    this.debit(key, neg(amount));
  }

  item(itemId: string, delta: Partial<ItemDelta>): void {
    const cur = this.items.get(itemId) ?? {
      qty: ZERO_QTY,
      value: ZERO,
      defQty: ZERO_QTY,
      defValue: ZERO,
    };
    this.items.set(itemId, {
      qty: addQty(cur.qty, delta.qty ?? ZERO_QTY),
      value: add(cur.value, delta.value ?? ZERO),
      defQty: addQty(cur.defQty, delta.defQty ?? ZERO_QTY),
      defValue: add(cur.defValue, delta.defValue ?? ZERO),
    });
  }

  build(type: EntryType): Effect {
    return {
      type,
      ledgers: [...this.ledgers.entries()].filter(([, v]) => v !== 0),
      items: [...this.items.entries()],
      voidedBy: null,
    };
  }
}

function binOf(it: ItemState, bin: Bin): { qty: Milli; value: Paisa } {
  return bin === 'stock'
    ? { qty: it.qty, value: it.value }
    : { qty: it.defQty, value: it.defValue };
}

function binLedger(bin: Bin): string {
  return bin === 'stock' ? LEDGER.STOCK : LEDGER.DEFECTIVE;
}

function binDelta(bin: Bin, qty: Milli, value: Paisa): Partial<ItemDelta> {
  return bin === 'stock' ? { qty, value } : { defQty: qty, defValue: value };
}

/** Cost of taking `qty` out of a bin. Throws if the bin does not hold enough. */
function removalCost(it: ItemState, bin: Bin, qty: Milli, what: string): Paisa {
  const b = binOf(it, bin);
  if (qty > b.qty) throw new InvariantError(`${what}: only ${b.qty} in ${bin}, need ${qty}`);
  return proportion(b.value, qty, b.qty);
}

/** Cost basis for goods coming back in: current average, or the last known unit cost when the bin is empty. */
function inboundCost(it: ItemState, qty: Milli): Paisa {
  if (it.qty > 0) return mulDivRound(it.value, qty, it.qty) as Paisa;
  return lineAmount(qty, it.lastUnitCost);
}

function assertLines(entry: SaleEntry | PurchaseEntry): void {
  const seen = new Set<string>();
  let total = ZERO;
  for (const line of entry.lines) {
    if (seen.has(line.itemId))
      throw new InvariantError(`${entry.type} ${entry.id}: duplicate item ${line.itemId}`);
    seen.add(line.itemId);
    if (line.qty <= 0) throw new InvariantError(`${entry.type} ${entry.id}: non-positive qty`);
    const unit =
      entry.type === 'sale'
        ? (line as SaleEntry['lines'][number]).unitPrice
        : (line as PurchaseEntry['lines'][number]).unitCost;
    if (unit < 0) throw new InvariantError(`${entry.type} ${entry.id}: negative unit amount`);
    if (line.amount !== lineAmount(line.qty, unit))
      throw new InvariantError(`${entry.type} ${entry.id}: line amount mismatch`);
    total = add(total, line.amount);
  }
  if (entry.lines.length === 0) throw new InvariantError(`${entry.type} ${entry.id}: no lines`);
  if (entry.total !== total) throw new InvariantError(`${entry.type} ${entry.id}: total mismatch`);
  if (entry.paid < 0 || entry.paid > entry.total)
    throw new InvariantError(`${entry.type} ${entry.id}: paid out of range`);
  if (entry.partyId === null && entry.paid !== entry.total)
    throw new InvariantError(`${entry.type} ${entry.id}: walk-in must be fully paid`);
}

function saleEffect(state: State, e: SaleEntry, b: EffectBuilder): void {
  assertLines(e);
  for (const line of e.lines) {
    const it = itemState(state, line.itemId);
    const cost = removalCost(it, 'stock', line.qty, `sale ${e.id} of ${line.itemId}`);
    b.debit(LEDGER.COGS, cost);
    b.credit(LEDGER.STOCK, cost);
    b.item(line.itemId, { qty: negQty(line.qty), value: neg(cost) });
  }
  b.debit(acctKey(e.accountId), e.paid);
  if (e.partyId !== null) b.debit(partyKey(e.partyId), add(e.total, neg(e.paid)));
  b.credit(LEDGER.SALES, e.total);
}

function purchaseEffect(e: PurchaseEntry, b: EffectBuilder): void {
  assertLines(e);
  for (const line of e.lines) {
    b.debit(LEDGER.STOCK, line.amount);
    b.item(line.itemId, { qty: line.qty, value: line.amount });
  }
  b.credit(acctKey(e.accountId), e.paid);
  if (e.partyId !== null) b.credit(partyKey(e.partyId), add(e.total, neg(e.paid)));
}

function returnEffect(state: State, e: ReturnEntry, b: EffectBuilder): void {
  if (e.qty <= 0) throw new InvariantError(`return ${e.id}: non-positive qty`);
  if (e.amount < 0) throw new InvariantError(`return ${e.id}: negative amount`);
  const it = itemState(state, e.itemId);
  const counterparty = e.partyId === null ? acctKey(e.accountId) : partyKey(e.partyId);
  if (e.kind === 'sale') {
    // Customer brings goods back: income reverses, goods come in at cost.
    b.debit(LEDGER.SALES_RETURNS, e.amount);
    b.credit(counterparty, e.amount);
    const cost = inboundCost(it, e.qty);
    b.debit(binLedger(e.bin), cost);
    b.credit(LEDGER.COGS, cost);
    b.item(e.itemId, binDelta(e.bin, e.qty, cost));
  } else {
    // We send goods back to a vendor: they owe us (or refund cash), goods leave at cost.
    b.debit(counterparty, e.amount);
    const cost = removalCost(it, e.bin, e.qty, `return ${e.id} of ${e.itemId}`);
    b.credit(binLedger(e.bin), cost);
    b.item(e.itemId, binDelta(e.bin, negQty(e.qty), neg(cost)));
    const diff = add(e.amount, neg(cost));
    if (diff > 0) b.credit(LEDGER.STOCK_ADJ, diff);
    else if (diff < 0) b.debit(LEDGER.STOCK_ADJ, neg(diff));
  }
}

function stockAdjustEffect(state: State, e: StockAdjustEntry, b: EffectBuilder): void {
  if (e.qty === 0) throw new InvariantError(`stock_adjust ${e.id}: zero qty`);
  const counter = e.reason === 'opening' ? LEDGER.OPENING : LEDGER.STOCK_ADJ;
  if (e.qty > 0) {
    if (e.unitCost === null || e.unitCost < 0)
      throw new InvariantError(`stock_adjust ${e.id}: unit cost required when adding`);
    const value = lineAmount(e.qty, e.unitCost);
    b.debit(binLedger(e.bin), value);
    b.credit(counter, value);
    b.item(e.itemId, binDelta(e.bin, e.qty, value));
  } else {
    const qty = negQty(e.qty);
    const cost = removalCost(
      itemState(state, e.itemId),
      e.bin,
      qty,
      `stock_adjust ${e.id} of ${e.itemId}`,
    );
    b.credit(binLedger(e.bin), cost);
    b.debit(counter, cost);
    b.item(e.itemId, binDelta(e.bin, negQty(qty), neg(cost)));
  }
}

/**
 * What a non-void entry does to the ledger, given the state it is applied to.
 * Pure. Throws InvariantError if the entry is malformed or impossible (which
 * commands prevent before an entry is ever created).
 */
export function computeEffect(state: State, entry: Exclude<Entry, { type: 'void' }>): Effect {
  const b = new EffectBuilder();
  switch (entry.type) {
    case 'sale':
      saleEffect(state, entry, b);
      break;
    case 'purchase':
      purchaseEffect(entry, b);
      break;
    case 'payment':
      if (entry.amount <= 0) throw new InvariantError(`payment ${entry.id}: non-positive amount`);
      if (entry.direction === 'in') {
        b.debit(acctKey(entry.accountId), entry.amount);
        b.credit(partyKey(entry.partyId), entry.amount);
      } else {
        b.debit(partyKey(entry.partyId), entry.amount);
        b.credit(acctKey(entry.accountId), entry.amount);
      }
      break;
    case 'return':
      returnEffect(state, entry, b);
      break;
    case 'expense':
      if (entry.amount <= 0) throw new InvariantError(`expense ${entry.id}: non-positive amount`);
      b.debit(LEDGER.EXPENSE, entry.amount);
      b.credit(acctKey(entry.accountId), entry.amount);
      break;
    case 'drawing':
      if (entry.amount <= 0) throw new InvariantError(`drawing ${entry.id}: non-positive amount`);
      b.debit(LEDGER.DRAWING, entry.amount);
      b.credit(acctKey(entry.accountId), entry.amount);
      break;
    case 'transfer':
      if (entry.amount <= 0) throw new InvariantError(`transfer ${entry.id}: non-positive amount`);
      if (entry.fromAccountId === entry.toAccountId)
        throw new InvariantError(`transfer ${entry.id}: same account`);
      b.debit(acctKey(entry.toAccountId), entry.amount);
      b.credit(acctKey(entry.fromAccountId), entry.amount);
      break;
    case 'stock_adjust':
      stockAdjustEffect(state, entry, b);
      break;
    case 'account_opening':
      b.debit(acctKey(entry.accountId), entry.amount);
      b.credit(LEDGER.OPENING, entry.amount);
      break;
    case 'party_opening':
      b.debit(partyKey(entry.partyId), entry.amount);
      b.credit(LEDGER.OPENING, entry.amount);
      break;
  }
  return b.build(entry.type);
}

export function negateEffect(effect: Effect, type: EntryType): Effect {
  return {
    type,
    ledgers: effect.ledgers.map(([k, v]) => [k, neg(v)] as const),
    items: effect.items.map(
      ([id, d]) =>
        [
          id,
          {
            qty: negQty(d.qty),
            value: neg(d.value),
            defQty: negQty(d.defQty),
            defValue: neg(d.defValue),
          },
        ] as const,
    ),
    voidedBy: null,
  };
}

export type EffectProblem =
  | { readonly code: 'unbalanced'; readonly sum: number }
  | { readonly code: 'negative_stock'; readonly itemId: string }
  | { readonly code: 'negative_defective'; readonly itemId: string }
  | { readonly code: 'negative_value'; readonly itemId: string }
  | { readonly code: 'orphan_value'; readonly itemId: string };

/**
 * Would applying this effect leave the books in an impossible state?
 * Used both by the void command (to give the user a message) and by `apply`
 * (as a hard invariant).
 */
export function effectProblem(state: State, effect: Effect): EffectProblem | null {
  let sum = 0;
  for (const [, v] of effect.ledgers) sum += v;
  if (sum !== 0) return { code: 'unbalanced', sum };
  for (const [itemId, d] of effect.items) {
    const it = itemState(state, itemId);
    const qty = it.qty + d.qty;
    const value = it.value + d.value;
    const defQty = it.defQty + d.defQty;
    const defValue = it.defValue + d.defValue;
    if (qty < 0) return { code: 'negative_stock', itemId };
    if (defQty < 0) return { code: 'negative_defective', itemId };
    if (value < 0 || defValue < 0) return { code: 'negative_value', itemId };
    if (qty === 0 && value !== 0) return { code: 'orphan_value', itemId };
    if (defQty === 0 && defValue !== 0) return { code: 'orphan_value', itemId };
  }
  return null;
}

export function describeProblem(p: EffectProblem): string {
  switch (p.code) {
    case 'unbalanced':
      return `postings do not balance (sum ${p.sum})`;
    case 'negative_stock':
      return `stock of ${p.itemId} would go negative`;
    case 'negative_defective':
      return `defective bin of ${p.itemId} would go negative`;
    case 'negative_value':
      return `stock value of ${p.itemId} would go negative`;
    case 'orphan_value':
      return `a bin of ${p.itemId} would be empty but still carry value`;
  }
}

// ---------------------------------------------------------------------------
// Apply / fold
// ---------------------------------------------------------------------------

function applyEffect(draft: Draft, effect: Effect): void {
  const problem = effectProblem(draft, effect);
  if (problem !== null) throw new InvariantError(describeProblem(problem));
  for (const [k, v] of effect.ledgers) {
    const next = add(draft.ledgers.get(k) ?? ZERO, v);
    if (next === 0) draft.ledgers.delete(k);
    else draft.ledgers.set(k, next);
  }
  for (const [itemId, d] of effect.items) {
    const it = draft.items.get(itemId) ?? EMPTY_ITEM;
    draft.items.set(itemId, {
      qty: addQty(it.qty, d.qty),
      value: add(it.value, d.value),
      defQty: addQty(it.defQty, d.defQty),
      defValue: add(it.defValue, d.defValue),
      lastUnitCost: it.lastUnitCost,
    });
  }
}

function setLastUnitCost(draft: Draft, itemId: string, unitCost: Paisa): void {
  const it = draft.items.get(itemId) ?? EMPTY_ITEM;
  draft.items.set(itemId, { ...it, lastUnitCost: unitCost });
}

function applyMut(draft: Draft, entry: Entry): void {
  if (entry.seq <= draft.lastSeq)
    throw new InvariantError(`entry ${entry.id}: seq ${entry.seq} not after ${draft.lastSeq}`);
  if (draft.effects.has(entry.id)) throw new InvariantError(`entry ${entry.id}: duplicate id`);

  let effect: Effect;
  if (entry.type === 'void') {
    const target = draft.effects.get(entry.targetId);
    if (!target) throw new InvariantError(`void ${entry.id}: unknown target ${entry.targetId}`);
    if (target.type === 'void') throw new InvariantError(`void ${entry.id}: cannot void a void`);
    if (target.voidedBy !== null)
      throw new InvariantError(`void ${entry.id}: target already voided`);
    effect = negateEffect(target, 'void');
    applyEffect(draft, effect);
    draft.effects.set(entry.targetId, { ...target, voidedBy: entry.id });
  } else {
    effect = computeEffect(draft, entry);
    applyEffect(draft, effect);
    if (entry.type === 'sale') {
      if (entry.no !== draft.nextSaleNo)
        throw new InvariantError(
          `sale ${entry.id}: expected no ${draft.nextSaleNo}, got ${entry.no}`,
        );
      draft.nextSaleNo += 1;
    } else if (entry.type === 'purchase') {
      if (entry.no !== draft.nextPurchaseNo)
        throw new InvariantError(
          `purchase ${entry.id}: expected no ${draft.nextPurchaseNo}, got ${entry.no}`,
        );
      draft.nextPurchaseNo += 1;
      for (const line of entry.lines) setLastUnitCost(draft, line.itemId, line.unitCost);
    } else if (
      entry.type === 'stock_adjust' &&
      entry.qty > 0 &&
      entry.unitCost !== null &&
      entry.bin === 'stock'
    ) {
      setLastUnitCost(draft, entry.itemId, entry.unitCost);
    }
  }
  draft.effects.set(entry.id, effect);
  draft.lastSeq = entry.seq;
  draft.count += 1;
}

function toDraft(state: State): Draft {
  return {
    ledgers: new Map(state.ledgers),
    items: new Map(state.items),
    effects: new Map(state.effects),
    nextSaleNo: state.nextSaleNo,
    nextPurchaseNo: state.nextPurchaseNo,
    lastSeq: state.lastSeq,
    count: state.count,
  };
}

/** Apply one entry, returning a new state. The input state is not touched. */
export function apply(state: State, entry: Entry): State {
  const draft = toDraft(state);
  applyMut(draft, entry);
  return draft;
}

/** Fold a whole khata (in seq order) into a state. */
export function fold(entries: Iterable<Entry>, from: State = emptyState()): State {
  const draft = toDraft(from);
  for (const entry of entries) applyMut(draft, entry);
  return draft;
}

/**
 * Incremental folder for bulk loading (seeds, imports): applies entries in place instead of
 * copying the state for every entry. The `state` it exposes is LIVE and changes on every
 * `push`; take `snapshot()` when you need a state that stays put.
 */
export class Folder {
  private readonly draft: Draft;
  constructor(from: State = emptyState()) {
    this.draft = toDraft(from);
  }
  get state(): State {
    return this.draft;
  }
  push(entry: Entry): void {
    applyMut(this.draft, entry);
  }
  snapshot(): State {
    return toDraft(this.draft);
  }
}

/** Sum of every ledger balance. Always 0 for a consistent state. */
export function trialBalanceSum(state: State): Paisa {
  let sum = ZERO;
  for (const v of state.ledgers.values()) sum = add(sum, v);
  return sum;
}

/** Full consistency check, for tests and the server health endpoint. Returns problems, empty when consistent. */
export function checkState(state: State): string[] {
  const problems: string[] = [];
  const tb = trialBalanceSum(state);
  if (tb !== 0) problems.push(`trial balance sum is ${tb}, not 0`);
  let stockValue = ZERO;
  let defValue = ZERO;
  for (const [id, it] of state.items) {
    if (it.qty < 0 || it.defQty < 0) problems.push(`item ${id} has negative quantity`);
    if (it.value < 0 || it.defValue < 0) problems.push(`item ${id} has negative value`);
    if (it.qty === 0 && it.value !== 0)
      problems.push(`item ${id} is empty but carries value ${it.value}`);
    if (it.defQty === 0 && it.defValue !== 0)
      problems.push(`item ${id} defective bin is empty but carries value ${it.defValue}`);
    stockValue = add(stockValue, it.value);
    defValue = add(defValue, it.defValue);
  }
  if (stockValue !== ledger(state, LEDGER.STOCK))
    problems.push(`stock ledger ${ledger(state, LEDGER.STOCK)} ≠ item values ${stockValue}`);
  if (defValue !== ledger(state, LEDGER.DEFECTIVE))
    problems.push(`defective ledger ${ledger(state, LEDGER.DEFECTIVE)} ≠ item values ${defValue}`);
  return problems;
}
